import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { useLogoutApi } from '@/api/auth';
import greenLogo from '@/assets/img/logo/greenLogo.svg';
import Logo from '@/assets/img/logo/logo_white.svg';
import * as S from '@/components/Header/style';
import { menuList } from '@/constants/menu';
import { breakpoints } from '@/styles/breakpoints';
import { colors } from '@/styles/color';
import useIsMobile from '@/hooks/useIsMobile';
import { useScroll } from '@/hooks/useScroll';
import { useAuthStore } from '@/store/authStore';
import { useModalStore } from '@/store/modalStore';
import { useTokenStore } from '@/store/tokenStore';

import MobileMenu from './MobileMenu/MobileMenu';
import HamburgerToggle from '../Icon/HamburgerToggle';
import LinkIcon from '../Icon/LinkIcon';

const Header = () => {
	const isScrolled = useScroll();
	const isMobile = useIsMobile(breakpoints.layout);
	const navigate = useNavigate();
	const { isLogin } = useTokenStore();
	const { name, clearValues } = useAuthStore();
	const { mutateAsync: logout } = useLogoutApi();
	const { pathname } = useLocation();
	const [isOpen, setIsOpen] = useState(false);
	const [headerHovered, setHeaderHovered] = useState(false);
	/** 데스크톱: 마우스가 올라간 1차 메뉴 열 인덱스 — 해당 열의 서브만 표시 */
	const [activeMenuIndex, setActiveMenuIndex] = useState<number | null>(null);
	const { showModal } = useModalStore();

	const isAuthPath =
		pathname.split('/')[1].toLowerCase() === 'auth' ||
		pathname.split('/')[1].toLowerCase() === 'mypage' ||
		pathname.split('/')[1].toLowerCase() === 'conference';
	/** 투명 히어로: 흰 글자 / 흰 배경(스크롤·메가메뉴·모바일 스크롤): 브랜드색 반전 대비 */
	const useLightNavText = !isAuthPath && !isScrolled && (isMobile || !headerHovered);
	/** 모바일 전체 메뉴 열림 — 하단 회원 패널(UserPanel)과 동일한 primary400 배경으로 이어 보이게 */
	const mobileMenuOpenSolid = isMobile && isOpen;
	const defaultColor = mobileMenuOpenSolid
		? '#fff'
		: isAuthPath
			? colors.textPrimary
			: useLightNavText
				? '#fff'
				: colors.primary400;
	const isTopHeroBar = !isAuthPath && !isScrolled && !(headerHovered && !isMobile);
	const backgroundColor = mobileMenuOpenSolid
		? colors.primary400
		: isAuthPath || (headerHovered && !isMobile) || isScrolled
			? '#fff'
			: 'transparent';
	const heroMenuContrast = isTopHeroBar && !headerHovered;
	const megaMenuOpen =
		!isMobile &&
		activeMenuIndex !== null &&
		Boolean(menuList[activeMenuIndex]?.subMenu?.length);

	/** GNB: 최상단 히어로(스크롤 전·헤더 비호버)만 흰색, 그 외 검정 */
	const gnbColor = isAuthPath ? colors.textPrimary : useLightNavText ? '#fff' : colors.textPrimary;

	const onClickSubMenu = (path: string, title: string) => {
		if (title === '회원가입') {
			if (isLogin()) {
				showModal('이미 화장품학회 회원이십니다.', 'confirm');
				return;
			} else {
				navigate('/auth/signup');
				return;
			}
		}
		if (title === '연회비 납부') {
			if (!isLogin()) {
				showModal('로그인이 필요합니다.', 'login', '로그인 하러가기', () => navigate('/auth/login'));
				return;
			}
		}
		navigate(path ?? '', { state: { menu: title } });
	};
	const getLogoSrc = () => {
		if (mobileMenuOpenSolid) {
			return Logo;
		}
		if (isAuthPath || (headerHovered && !isMobile) || isScrolled) {
			return greenLogo;
		}
		return Logo;
	};
	const handleMenuClick = (path: string) => {
		if (!path) return;
		navigate(path);
	};
	const displayMemberName = (name || '회원').trim();
	const handleLogout = async () => {
		try {
			const res = await logout();
			if (res.data.success) {
				clearValues();
				navigate('/');
			}
		} catch (error) {
			console.error('로그아웃 중 오류가 발생했습니다:', error);
		}
	};
	const renderMenuItems = () =>
		menuList.map((menu, index) => (
			<S.MenuColumn
				key={index}
				data-state={megaMenuOpen && activeMenuIndex === index ? 'open' : undefined}
				onMouseEnter={() => setActiveMenuIndex(index)}
			>
				<S.MenuItem
					defaultColor={gnbColor}
					onHero={heroMenuContrast}
					onClick={() => handleMenuClick(menu.mainPath ?? '')}
				>
					{menu.mainPath === 'link' ? (
						<>
							{menu.mainMenu}
							<LinkIcon iconColor={gnbColor} width="20" height="20" />
						</>
					) : (
						menu.mainMenu
					)}
				</S.MenuItem>

				{megaMenuOpen && activeMenuIndex === index && menu.subMenu && (
					<S.SubMenuList>
						{menu.subMenu.map((subMenu, subIndex) => (
							<S.MenuItem
								className="sub"
								key={subIndex}
								defaultColor={gnbColor}
								onHero={false}
								onClick={() => onClickSubMenu(subMenu.path ?? '', subMenu.title ?? '')}
							>
								{subMenu.title}
							</S.MenuItem>
						))}
					</S.SubMenuList>
				)}
			</S.MenuColumn>
		));
	const renderAuthItems = () =>
		!isMobile ? (
			<>
				{isLogin() ? (
					<S.UserMenuRow>
						<S.UserNameButton type="button" defaultColor={gnbColor} onHero={heroMenuContrast} onClick={() => navigate('/mypage')}>
							{displayMemberName}님
						</S.UserNameButton>
						<S.UserMenuDivider defaultColor={gnbColor} aria-hidden />
						<S.AuthButton type="button" defaultColor={gnbColor} onHero={heroMenuContrast} onClick={handleLogout}>
							로그아웃
						</S.AuthButton>
					</S.UserMenuRow>
				) : (
					<>
						<S.AuthButton defaultColor={defaultColor} onHero={heroMenuContrast} type="button" onClick={() => onClickSubMenu('/auth/login', '로그인')}>
							로그인
						</S.AuthButton>
						<S.AuthButton defaultColor={defaultColor} onHero={heroMenuContrast} type="button" onClick={() => onClickSubMenu('/auth/signup', '회원가입')}>
							회원가입
						</S.AuthButton>
					</>
				)}
			</>
		) : (
			<>
				{!isLogin() && (
					<S.AuthButton defaultColor={defaultColor} onHero={heroMenuContrast} type="button" onClick={() => onClickSubMenu('/auth/login', '로그인')}>
						로그인
					</S.AuthButton>
				)}
				<HamburgerToggle
					open={isOpen}
					color={defaultColor}
					onClick={() => toggleMobileMenu(!isOpen)}
					ariaExpanded={isOpen}
					ariaLabel={isOpen ? '메뉴 닫기' : '메뉴 열기'}
				/>
			</>
		);
	const toggleMobileMenu = (status: boolean) => {
		if (status === true) {
			setIsOpen(true);
			document.body.classList.add('hidden');
		} else {
			setIsOpen(false);
			document.body.classList.remove('hidden');
		}
	};
	useEffect(() => {
		if (isOpen) {
			document.body.classList.remove('hidden');
			setIsOpen(false);
		}
	}, [isMobile]);

	useEffect(() => {
		if (isMobile) setActiveMenuIndex(null);
	}, [isMobile]);

	return (
		<>
			<S.HeaderContainer
				id="header"
				isHover={false}
				isAuthPath={isAuthPath}
				defaultColor={backgroundColor}
				isScrolled={isScrolled}
				transparentBar={isTopHeroBar && !mobileMenuOpenSolid}
				$menuOpen={isMobile && isOpen}
				onMouseEnter={() => setHeaderHovered(true)}
				onMouseLeave={() => {
					setHeaderHovered(false);
					setActiveMenuIndex(null);
				}}
			>
				<S.Nav isHover={false}>
					<S.LogoBox
						className={headerHovered ? 'hover' : ''}
						onMouseEnter={() => setActiveMenuIndex(null)}
					>
						<Link to="/">
							<S.ImgBox>
								<img src={getLogoSrc()} alt="Logo" />
							</S.ImgBox>
						</Link>
					</S.LogoBox>
					<S.NavCenter>
						<S.MenuList menuCount={menuList.length}>{renderMenuItems()}</S.MenuList>
					</S.NavCenter>
					<S.AuthBox isLogin={isLogin()} onMouseEnter={() => setActiveMenuIndex(null)}>
						{renderAuthItems()}
					</S.AuthBox>
				</S.Nav>
			</S.HeaderContainer>

			<MobileMenu isOpen={isOpen} onClickIsOpen={() => toggleMobileMenu(false)} />
		</>
	);
};

export default Header;
